import { Box } from '@inflearn/ds-react-next';
import type { Language } from '@inflearn/i18n-tools';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import {
  type ClientApiV1ReviewsCourseUserUserIdReceivedQueryString,
  fetchClientApiV1ReviewsCourseUserUserIdReceived,
} from '../../../../resources/@types/uccSchema';
import { logger } from '../../../../resources/Logger';
import EmptyStates from '../../../components/common/EmptyStates';
import CustomHead from '../../../components/layout/CustomHead';
import Layout from '../../../components/layout/Layout';
import CourseReviewItem from '../../../components/pages/profile/CourseReviewItem/CourseReviewItem';
import CourseReviewSkeletonItem from '../../../components/pages/profile/CourseReviewItem/CourseReviewSkeletonItem';
import Pagination from '../../../components/pages/profile/Pagination/Pagination';
import SectionHeader from '../../../components/pages/profile/SectionHeader/SectionHeader';
import useReviewsCourseUserIdReceivedQuery, {
  getUseReviewsCourseUserIdReceivedQueryOptions,
} from '../../../components/pages/profile/hooks/query/useReviewsCourseUserIdReceivedQuery';
import { DeferredComponent } from '../../../components/util/DeferredComponent';
import { USERS_URL } from '../../../constants/urls';
import { useCustomQueryParams } from '../../../hooks/useCustomQueryParams';
import useUserProfileQuery from '../../../hooks/useUserProfileQuery';
import {
  type WithCommonNextPage,
  withProfileUserProps,
} from '../../../utils/page/withProfileUserProps';
import { withRemovedPageFallback } from '../../../utils/page/withRemovedPageFallback';

const DEFAULT_QUERY_PARAMS = {
  pageSize: 10,
  pageNumber: 1,
};

const UserReviewsPage: WithCommonNextPage = () => {
  const { t } = useTranslation();
  const { query } = useRouter();
  const { data: profile } = useUserProfileQuery({ id: Number(query.id) });
  const { queryParams, push } =
    useCustomQueryParams<ClientApiV1ReviewsCourseUserUserIdReceivedQueryString>({
      defaultValue: DEFAULT_QUERY_PARAMS,
      queryParamKeys: ['pageNumber', 'pageSize'],
    });

  const pageNumber = Number(queryParams.pageNumber);

  const { data, isError, isLoading, refetch } = useReviewsCourseUserIdReceivedQuery({
    userId: String(profile?.id ?? ''),
    queryParams,
    options: {
      keepPreviousData: true,
      enabled: Boolean(profile),
    },
  });

  const totalPage = data?.totalPage ?? 1;

  const userName = profile?.name;

  return (
    <Layout isInstructor={Boolean(profile?.isInstructor)} isInactive={Boolean(profile?.isInactive)}>
      <CustomHead
        title={t('{{userName}}님의 수강평', { userName: userName })}
        description={t(
          '인프런 {{userName}}님의 수강평 페이지 입니다. - {{userName}}님 소개 | 인프런',
          { userName: userName },
        )}
        image={profile?.thumbnailUrl}
      />
      <SectionHeader>
        <SectionHeader.Title title={t('수강평')} />
      </SectionHeader>
      <EmptyStates height={300} isError={isError} onRetry={refetch}>
        <Box component="ul">
          {isLoading ? (
            <DeferredComponent>
              <CourseReviewSkeletonItem count={5} />
            </DeferredComponent>
          ) : (
            data?.items?.map((review) => (
              <Box
                mb={{
                  base: 8,
                  sm: 16,
                }}
                component="li"
                key={review.id}
              >
                <CourseReviewItem fullContent {...review} />
              </Box>
            ))
          )}
        </Box>
      </EmptyStates>
      {totalPage === DEFAULT_QUERY_PARAMS.pageNumber ? null : (
        <Pagination
          value={pageNumber}
          total={totalPage}
          onChange={(page) => push({ pageNumber: page }, { scroll: true })}
        />
      )}
    </Layout>
  );
};

export const getServerSideProps = withProfileUserProps(
  async ({ context, queryClient, isInstructor }) => {
    const userId = String(context.params?.id || '');
    const pageNumber = context.query.pageNumber;
    const lang = context.locale as Language;

    if (!isInstructor) {
      return {
        redirect: {
          destination: USERS_URL.HOME({ userId: userId, lang }),
          permanent: false,
        },
      };
    }

    try {
      const queryParams = {
        ...DEFAULT_QUERY_PARAMS,
        ...(pageNumber && { pageNumber: Number(pageNumber) }),
        lang,
      };

      const queryOptions = getUseReviewsCourseUserIdReceivedQueryOptions(
        String(userId),
        queryParams,
      );

      await queryClient.prefetchQuery(queryOptions.queryKey, () =>
        fetchClientApiV1ReviewsCourseUserUserIdReceived(queryOptions.url),
      );

      return {
        props: {},
      };
    } catch (error) {
      logger.info(`[ERROR in SSR reviews:] ${error}`);

      return {
        props: {},
      };
    }
  },
);

export default withRemovedPageFallback(UserReviewsPage);
